import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'

/**
 * Registration Wizard Store
 *
 * Maneja el estado del wizard de registro paso a paso.
 * Persistido en localStorage para recuperar el progreso si el usuario
 * cierra el navegador durante el proceso.
 *
 * Flujo:
 *  Step 0: Credenciales (email/password)
 *  Step 1: Datos de la empresa
 *  Step 2: Datos financieros
 *  Step 3: Canales digitales
 *  Step 4: Confirmación / Procesando
 */

export interface RegistrationData {
	// Step 0 - Credenciales
	email: string
	password: string
	supabaseId?: string
	emailConfirmed: boolean

	// Step 1 - Empresa
	companyName: string
	documentType: string
	employeeCount: string
	position: string
	country: string
	operatingTime: string
	revenueLevel: string
	businessSegment: string

	// Step 2 - Finanzas
	isProfitable: string
	hasClearKPIs: string
	knowsCAC: string
	salesChannel: string
	productType: string
	salesModel: string
	grossMargin: string
	hasDebt: string

	// Step 3 - Canales
	website: string
	instagram: string
	facebook: string
	tiktok: string
	linkedin: string
	otherChannel: string
}

export type RegistrationStep = 0 | 1 | 2 | 3 | 4

interface RegistrationState {
	currentStep: RegistrationStep
	data: Partial<RegistrationData>
	isLoading: boolean
	error: string | null
	isHydrated: boolean
}

interface RegistrationActions {
	setStep: (step: RegistrationStep) => void
	nextStep: () => void
	prevStep: () => void
	updateData: (data: Partial<RegistrationData>) => void
	setSupabaseId: (id: string) => void
	setEmailConfirmed: (confirmed: boolean) => void
	setLoading: (loading: boolean) => void
	setError: (error: string | null) => void
	clearError: () => void
	reset: () => void
	setHydrated: (hydrated: boolean) => void
}

type RegistrationStore = RegistrationState & RegistrationActions

const initialState: RegistrationState = {
	currentStep: 0,
	data: {},
	isLoading: false,
	error: null,
	isHydrated: false,
}

export const useRegistrationStore = create<RegistrationStore>()(
	devtools(
		persist(
			(set) => ({
				...initialState,

				setStep: (step) => set({ currentStep: step }, false, 'registration/setStep'),

				nextStep: () =>
					set(
						(state) => ({
							currentStep: Math.min(state.currentStep + 1, 4) as RegistrationStep,
						}),
						false,
						'registration/nextStep'
					),

				prevStep: () =>
					set(
						(state) => ({
							currentStep: Math.max(state.currentStep - 1, 0) as RegistrationStep,
						}),
						false,
						'registration/prevStep'
					),

				updateData: (data) =>
					set(
						(state) => ({
							data: { ...state.data, ...data },
						}),
						false,
						'registration/updateData'
					),

				setSupabaseId: (id) =>
					set(
						(state) => ({
							data: { ...state.data, supabaseId: id },
						}),
						false,
						'registration/setSupabaseId'
					),

				setEmailConfirmed: (confirmed) =>
					set(
						(state) => ({
							data: { ...state.data, emailConfirmed: confirmed },
						}),
						false,
						'registration/setEmailConfirmed'
					),

				setLoading: (isLoading) => set({ isLoading }, false, 'registration/setLoading'),

				setError: (error) => set({ error }, false, 'registration/setError'),

				clearError: () => set({ error: null }, false, 'registration/clearError'),

				reset: () => set({ ...initialState, isHydrated: true }, false, 'registration/reset'),

				setHydrated: (isHydrated) => set({ isHydrated }, false, 'registration/setHydrated'),
			}),
			{
				name: 'registration-storage',
				partialize: (state) => ({
					currentStep: state.currentStep,
					data: state.data,
				}),
				onRehydrateStorage: () => (state) => {
					state?.setHydrated(true)
				},
			}
		),
		{ name: 'RegistrationStore' }
	)
)

// Selectores útiles
export const selectCurrentStep = (state: RegistrationStore) => state.currentStep
export const selectRegistrationData = (state: RegistrationStore) => state.data
export const selectIsLoading = (state: RegistrationStore) => state.isLoading
export const selectError = (state: RegistrationStore) => state.error
export const selectEmailConfirmed = (state: RegistrationStore) => state.data.emailConfirmed ?? false
